import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {QueryCacheKeys} from '../../QueryKey'
import useQueryVolumeLeague from './useQueryVolumeLeague'
import {getVolumeLeagueTrader} from '../../../apis/volume_league/trader'
import {ITraderMapVolume} from '../../../apis/tradermap/volume'

/**
 * 임시: admin/trader/volume 대체. league id 기준 트레이더 순위를 ITraderMapVolume 형태로 변환.
 * - volume: 응답에 없으므로 share/100 * 리그 total_volume 으로 계산
 * - buy/sell: symbol scope 리그만 buy_sell_ratio 로 추정 (그 외 scope 는 비율 미제공 → 미표시)
 * - trade_count: 제공되지 않음
 * - ranked=false 면 data 없음 → 기존 'No Volume Data' 처리
 */
const useQueryVolumeLeagueTraderVolume = ({league_id, user_id}: {league_id?: string; user_id?: string}) => {
    const {data: leagues} = useQueryVolumeLeague()
    const query = useQuery({
        enabled: !!league_id && !!user_id,
        queryKey: QueryCacheKeys.volumeLeague.getVolumeLeagueTrader(league_id, user_id),
        queryFn: async () => {
            const {data} = await getVolumeLeagueTrader(league_id, user_id)
            return data
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const data = useMemo((): ITraderMapVolume | undefined => {
        const ranking = query.data?.ranked ? query.data?.ranking : undefined
        if (!ranking) return undefined
        const league = leagues?.find(i => String(i?.id) === league_id)
        const volume = (ranking.share / 100) * (league?.total_volume || 0)
        const buyRatio = league?.scope_type === 'symbol' ? ranking.buy_sell_ratio : undefined
        return {
            rank: ranking.rank,
            symbol: league?.symbol,
            volume,
            volume_share: ranking.share,
            buy_volume: buyRatio !== undefined ? (volume * buyRatio) / 100 : undefined,
            sell_volume: buyRatio !== undefined ? (volume * (100 - buyRatio)) / 100 : undefined,
        }
    }, [query.data, leagues, league_id])

    return {data, isLoading: query.isLoading}
}

export default useQueryVolumeLeagueTraderVolume
